import React from "react";
import { Button } from "@/components/ui/button";
import { Eye, FileText, Loader2, FileSpreadsheet } from "lucide-react";
import { ReportType, ReportGenerationOptions } from "../types";

interface ReportActionsProps {
  reportOptions: ReportGenerationOptions;
  isGeneratingReport: boolean;
  isLoadingData: boolean;
  canPreview: boolean;
  onPreview: () => void;
  onGenerateReport: (reportType: ReportType) => void;
}

export const ReportActions: React.FC<ReportActionsProps> = ({
  reportOptions,
  isGeneratingReport,
  isLoadingData,
  canPreview,
  onPreview,
  onGenerateReport,
}) => {
  const handleGeneratePdf = () => {
    // Determine report type based on selected options
    let reportType: ReportType;
    if (reportOptions.includeNAVSummary && reportOptions.includeHoldings) {
      reportType = ReportType.COMBINED_REPORT;
    } else if (reportOptions.includeNAVSummary) {
      reportType = ReportType.NAV_SUMMARY;
    } else if (reportOptions.includeHoldings) {
      reportType = ReportType.HOLDINGS_DETAIL;
    } else {
      // Default to combined if nothing selected
      reportType = ReportType.COMBINED_REPORT;
    }

    onGenerateReport(reportType); // defaults to PDF
  };

  const handleGenerateCsv = () => {
    // Re-use same logic for determining report type
    let reportType: ReportType;
    if (reportOptions.includeNAVSummary && reportOptions.includeHoldings) {
      reportType = ReportType.COMBINED_REPORT;
    } else if (reportOptions.includeNAVSummary) {
      reportType = ReportType.NAV_SUMMARY;
    } else if (reportOptions.includeHoldings) {
      reportType = ReportType.HOLDINGS_DETAIL;
    } else {
      reportType = ReportType.COMBINED_REPORT;
    }

    // Explicitly request CSV format
    (onGenerateReport as any)(reportType, "CSV");
  };

  return (
    <div className="pt-4 border-t space-y-3">
      <Button
        onClick={onPreview}
        variant="outline"
        className="w-full"
        disabled={!canPreview || isLoadingData}
      >
        <Eye className="mr-2 h-4 w-4" />
        {isLoadingData ? "Loading Data..." : "Preview Report Data"}
      </Button>

      <Button
        onClick={handleGeneratePdf}
        className="w-full"
        disabled={isGeneratingReport || isLoadingData}
      >
        {isGeneratingReport ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating Report...
          </>
        ) : (
          <>
            <FileText className="mr-2 h-4 w-4" />
            Generate & Download Report
          </>
        )}
      </Button>

      <Button
        onClick={handleGenerateCsv}
        variant="secondary"
        className="w-full"
        disabled={isGeneratingReport || isLoadingData}
      >
        {isGeneratingReport ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating Report...
          </>
        ) : (
          <>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export CSV
          </>
        )}
      </Button>
    </div>
  );
}; 